import { Icon, Text } from "@certn/cascadia-react";
import { CascadiaStandardIcon } from "@certn/cascadia-tokens";
import { css } from "@emotion/css";
import { mergeProps } from "@react-aria/utils";
import { useMemo } from "react";
import { MenuItem, MenuItemProps } from "react-aria-components";

interface ListItemProps extends MenuItemProps {
  icon: CascadiaStandardIcon;
  isCompleted?: boolean;
  isInvalid?: boolean;
  testID?: string;
  sectionKey?: string;
}

export function ApplicationSectionListItem(props: ListItemProps) {
  const {
    icon,
    isCompleted,
    isInvalid,
    testID = "section-list-item",
    sectionKey,
    id,
    ...menuItemProps
  } = props;
  // Prefer `sectionKey` when a caller sets it; fall back to RAC's `id`.
  // ApplicationSectionList historically passed only `id`, which left every
  // nav entry as `section-list-item-undefined`.
  const testIdKey = sectionKey ?? (id == null ? undefined : String(id));
  const className = useMemo(
    () =>
      css(styles.listItem, {
        "&[data-focus-visible]": {
          boxShadow: "var(--focus-action)",
        },
        "&[data-hovered]": {
          backgroundColor: "var(--background-ghost-hover)",
        },
        "&[data-selected]": {
          background: "var(--background-selected-weak)",
        },
        "&[data-disabled]": {
          cursor: "not-allowed",
        },
      }),
    []
  );

  return (
    <MenuItem
      {...mergeProps(menuItemProps, { className })}
      id={id}
      data-testid={`${testID}-${testIdKey}`}
    >
      {(renderProps) => {
        const iconType = renderProps.isDisabled
          ? "lock"
          : isInvalid
            ? "warning"
            : isCompleted
              ? "check_circle"
              : icon;

        return (
          <>
            <Icon
              name={iconType}
              padding="space-05"
              color={
                renderProps.isDisabled
                  ? "icon-disabled"
                  : isInvalid
                    ? "icon-warning"
                    : isCompleted
                      ? "icon-success"
                      : "icon-weak"
              }
              fill={isCompleted && !isInvalid}
            />

            <Text
              style="summary/nav-list"
              color={renderProps.isDisabled ? "text-disabled" : "text-base"}
            >
              {typeof props.children === "function"
                ? props.children(renderProps)
                : props.children}
            </Text>
          </>
        );
      }}
    </MenuItem>
  );
}

const styles = {
  listItem: css({
    display: "flex",
    alignItems: "center",
    padding: `${"var(--space-2)"} ${"var(--space-3)"}`,
    gap: "var(--space-2)",
    borderRadius: "var(--border-radius-2)",
    outline: "none",
    cursor: "pointer",
  }),
};
